import React, { useState } from 'react';
import Input from './Input';
import Button from './Button';
import Icon from '../AppIcon';
import { subscriptionService } from '../../services/subscriptionService';

const SubscriptionForm = ({ onSubmit, isModal = false, onClose }) => {
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    organization: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex?.test(email);
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors?.[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!validateEmail(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.firstName?.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData?.lastName?.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      // Call Supabase service with detailed form data
      const result = await subscriptionService?.subscribe(formData);

      if (result?.success) {
        setSubmitSuccess(true);
        setSuccessMessage(result?.message);
        setFormData({
          email: '',
          firstName: '',
          lastName: '',
          organization: '',
        });

        setTimeout(() => {
          setSubmitSuccess(false);
          setSuccessMessage('');
          if (isModal && onClose) {
            onClose();
          }
        }, 5000);
      } else {
        setErrors({
          submit: result?.message || 'Subscription failed. Please try again.'
        });
      }
    } catch (error) {
      console.error('Subscription error:', error);
      setErrors({
        submit: error?.message || 'An error occurred. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitSuccess) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <div className="flex items-center justify-center w-16 h-16 bg-success/10 rounded-full mb-4">
          <Icon name="CheckCircle" size={32} color="var(--color-success)" />
        </div>
        <h3 className="text-2xl font-heading font-semibold text-foreground mb-2">
          Welcome to THEPS Newsletter!
        </h3>
        <p className="text-muted-foreground max-w-md">
          {successMessage || 'Thank you for subscribing. You will receive our latest funding opportunities and research insights directly to your inbox.'}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {isModal && (
        <div className="mb-6">
          <h2 className="text-2xl font-heading font-semibold text-foreground mb-2">
            Subscribe to THEPS Newsletter
          </h2>
          <p className="text-sm text-muted-foreground">
            Stay informed about the latest funding opportunities and research
            insights
          </p>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          type="text"
          name="firstName"
          label="First Name"
          placeholder="Enter your first name"
          value={formData?.firstName}
          onChange={handleChange}
          error={errors?.firstName}
          required
          disabled={isSubmitting}
        />

        <Input
          type="text"
          name="lastName"
          label="Last Name"
          placeholder="Enter your last name"
          value={formData?.lastName}
          onChange={handleChange}
          error={errors?.lastName}
          required
          disabled={isSubmitting}
        />
      </div>
      <Input
        type="email"
        name="email"
        label="Email Address"
        placeholder="your.email@example.com"
        description="We'll never share your email with anyone else"
        value={formData?.email}
        onChange={handleChange}
        error={errors?.email}
        required
        disabled={isSubmitting}
        className="text-[#000000] placeholder:text-[#000000]"
      />
      <Input
        type="text"
        name="organization"
        label="Organization (Optional)"
        placeholder="Your organization or institution"
        value={formData?.organization}
        onChange={handleChange}
        disabled={isSubmitting}
      />
      {errors?.submit && (
        <div className="flex items-center gap-2 p-3 bg-error/10 border border-error/20 rounded-lg text-sm text-error">
          <Icon name="AlertCircle" size={16} />
          <span>{errors?.submit}</span>
        </div>
      )}
      <div className="flex items-center gap-3 pt-2">
        <Button
          type="submit"
          variant="default"
          size="lg"
          loading={isSubmitting}
          disabled={isSubmitting}
          iconName="Send"
          iconPosition="right"
          className="bg-accent hover:bg-accent/90 text-accent-foreground"
        >
          {isSubmitting ? 'Subscribing...' : 'Subscribe Now'}
        </Button>

        {isModal && onClose && (
          <Button
            type="button"
            variant="outline"
            size="lg"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
        )}
      </div>
      <div className="flex items-center justify-center">
        <p className="text-xs font-medium" style={{ color: '#6ba3e8' }}>
          No spam, unsubscribe anytime. Privacy guaranteed.
        </p>
      </div>
      <p className="text-xs text-muted-foreground">
        By subscribing, you agree to receive email communications from THEPS
        Newsletter. You can unsubscribe at any time.
      </p>
    </form>
  );
};

export default SubscriptionForm;